import { ReactNode } from 'react'

import { cn } from '@/lib/utils'

export interface Column<T> {
  label: string
  cell: (row: T) => ReactNode
  className?: string
  /** `cards`: left out of the phone card (e.g. a column that repeats the title). */
  hideOnCard?: boolean
}

/**
 * The admin index table, as the Orders and Stock pages draw it. `cards`: below `sm` each row is a card instead (first
 * column as its title, the rest as label / value lines); wide numeric tables leave it off and scroll sideways.
 */
export const OfficeTable = <T,>({
  rows,
  columns,
  rowKey,
  empty = 'Nothing here.',
  loading,
  onRowClick,
  cards = false,
}: {
  rows: T[]
  columns: Column<T>[]
  rowKey: (row: T, i: number) => string
  empty?: string
  loading?: boolean
  onRowClick?: (row: T) => void
  cards?: boolean
}) => (
  <>
  {cards && (
    <ul className={cn('space-y-2 sm:hidden', loading && 'opacity-60')}>
      {rows.map((row, i) => {
        const [title, ...rest] = columns
        return (
          <li
            key={rowKey(row, i)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn('rounded-md border border-border bg-card p-3 text-sm shadow-z1', onRowClick && 'cursor-pointer active:bg-subdued')}
          >
            <div className="font-medium text-ink">{title.cell(row)}</div>
            <dl className="mt-2 space-y-1.5">
              {rest
                .filter((c) => !c.hideOnCard)
                .map((c) => (
                  <div key={c.label} className="flex items-center justify-between gap-3 empty:hidden">
                    {c.label && <dt className="shrink-0 text-xs text-muted-foreground">{c.label}</dt>}
                    <dd className="ml-auto min-w-0 text-right">{c.cell(row)}</dd>
                  </div>
                ))}
            </dl>
          </li>
        )
      })}
      {!loading && rows.length === 0 && <li className="py-10 text-center text-sm text-muted-foreground">{empty}</li>}
    </ul>
  )}
  <div className={cn('overflow-x-auto rounded-md border border-border bg-card shadow-z1', cards && 'hidden sm:block')}>
    <table className="w-full text-sm">
      <thead className="bg-subdued text-left text-xs text-muted-foreground">
        <tr className="[&>th]:h-9 [&>th]:whitespace-nowrap [&>th]:border-b [&>th]:border-border [&>th]:px-4 [&>th]:font-medium">
          {columns.map((c) => (
            <th key={c.label} className={c.className}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className={cn(loading && 'opacity-60')}>
        {rows.map((row, i) => (
          <tr
            key={rowKey(row, i)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={cn('border-b border-border-subtle last:border-0 [&>td]:h-11 [&>td]:px-4', onRowClick && 'cursor-pointer hover:bg-subdued')}
          >
            {columns.map((c) => (
              <td key={c.label} className={c.className}>
                {c.cell(row)}
              </td>
            ))}
          </tr>
        ))}
        {!loading && rows.length === 0 && (
          <tr>
            <td colSpan={columns.length} className="py-10 text-center text-muted-foreground">
              {empty}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
  </>
)
